import type { TranslationKeys } from '@src/types/i18n';
import { useTranslation as useI18nTranslation } from 'react-i18next';

/**
 * 型安全な翻訳フック
 *
 * react-i18nextのuseTranslationフックを拡張して、
 * TypeScriptでの型安全性を向上させます。
 *
 * @returns {Object} 翻訳関数とi18nインスタンス
 * @returns {Function} returns.t - 翻訳関数（型安全）
 * @returns {Object} returns.i18n - i18nインスタンス
 * @returns {boolean} returns.ready - 翻訳の準備が完了しているかどうか
 *
 * @example
 * ```tsx
 * const { t, i18n, ready } = useTranslation();
 *
 * if (!ready) return <div>Loading...</div>;
 *
 * return <div>{t('common.submit')}</div>;
 * ```
 */
export function useTranslation() {
  const { t, i18n, ready } = useI18nTranslation();

  // 型安全な翻訳関数
  const typedT = (key: TranslationKeys, options?: Record<string, unknown>): string => {
    return t(key, options) as string;
  };

  return {
    t: typedT,
    i18n,
    ready,
  };
}

/**
 * 言語切り替え用のカスタムフック
 *
 * 言語の切り替え機能を提供します。
 *
 * @returns {Object} 言語切り替え関連の関数と状態
 * @returns {Function} returns.changeLanguage - 言語を切り替える関数
 * @returns {string} returns.currentLanguage - 現在の言語
 * @returns {boolean} returns.isJapanese - 現在の言語が日本語かどうか
 * @returns {boolean} returns.isEnglish - 現在の言語が英語かどうか
 *
 * @example
 * ```tsx
 * const { changeLanguage, currentLanguage, isJapanese } = useLanguageSwitcher();
 *
 * return (
 *   <button onClick={() => changeLanguage('en')}>
 *     Switch to English
 *   </button>
 * );
 * ```
 */
export function useLanguageSwitcher() {
  const { i18n } = useI18nTranslation();

  const changeLanguage = (language: 'ja' | 'en') => {
    i18n.changeLanguage(language);
  };

  const currentLanguage = i18n.language;
  const isJapanese = currentLanguage === 'ja' || currentLanguage.startsWith('ja');
  const isEnglish = currentLanguage === 'en' || currentLanguage.startsWith('en');

  return {
    changeLanguage,
    currentLanguage,
    isJapanese,
    isEnglish,
  };
}
